import { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, Copy, EnvelopeSimple, Camera, LinkSimple } from "@phosphor-icons/react";

export default function RequestPhotosModal({ jobId, defaultMessage = "", defaultEmail = "", onClose, onCreated }) {
  const [message, setMessage] = useState(defaultMessage);
  const [tenantEmail, setTenantEmail] = useState(defaultEmail);
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get(`/jobs/${jobId}/photo-request`).then(r => {
      if (r.data.request) setExisting(r.data.request);
    }).catch(() => {});
  }, [jobId]);

  const create = async (deliver) => {
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${jobId}/photo-request`, {
        deliver, tenant_email: tenantEmail, message,
      });
      setResult(r.data);
      if (deliver === "email" && r.data.email_sent) {
        toast.success(`Emailed to ${r.data.to_email}`);
      } else if (deliver === "email" && !r.data.email_sent) {
        toast.error("Email failed — tenant email may be missing. Use Copy Link instead.");
      }
      onCreated?.(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Link copied — paste into your text app");
    } catch { toast.error("Copy failed"); }
  };

  const link = result?.link || existing?.link;

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/80 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="request-photos-modal">
      <div className="bg-white w-full max-w-lg shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="p-4 bg-slate-900 text-white flex items-start justify-between">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest opacity-70 flex items-center gap-1">
              <Camera weight="fill" size={11}/> Request photos from tenant
            </div>
            <div className="mt-1 font-black text-lg">Get eyes on it first</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center hover:bg-white/10"><X size={18}/></button>
        </div>

        <div className="p-4 space-y-3">
          {existing && !result && (
            <div className="p-3 bg-emerald-50 border-2 border-emerald-500" data-testid="existing-request">
              <div className="text-[10px] font-black uppercase tracking-widest text-emerald-800">Active request already sent</div>
              <div className="text-[12px] text-emerald-900 mt-1">
                Opened by tenant: <b>{existing.opened_at ? "Yes" : "Not yet"}</b>
                {" · "}Photos uploaded: <b>{existing.uploaded_count}</b>
              </div>
              <div className="mt-2 flex items-center gap-1.5 bg-white p-1.5 border border-emerald-300">
                <LinkSimple size={12} className="text-slate-500"/>
                <input value={existing.link} readOnly data-testid="existing-link"
                  className="flex-1 text-[11px] font-mono outline-none min-w-0"/>
                <button onClick={() => copy(existing.link)} data-testid="copy-existing-link"
                  className="px-2 h-7 bg-slate-900 text-white text-[9px] font-black uppercase tracking-widest">
                  Copy
                </button>
              </div>
            </div>
          )}

          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Optional message to tenant</label>
            <textarea value={message} onChange={e => setMessage(e.target.value)} rows={3}
              placeholder="e.g. Please snap a clear photo of the leak under the sink + the fridge model sticker (usually inside the fridge on the left wall)."
              data-testid="request-message"
              className="mt-1 w-full border-2 border-slate-300 p-2 text-sm outline-none focus:border-slate-900"/>
          </div>

          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Tenant email (for email delivery)</label>
            <input type="email" value={tenantEmail} onChange={e => setTenantEmail(e.target.value)}
              placeholder="marcus@example.com" data-testid="tenant-email"
              className="mt-1 w-full h-10 border-2 border-slate-300 px-2 text-sm outline-none focus:border-slate-900"/>
          </div>

          <div className="grid grid-cols-2 gap-2 pt-1">
            <button onClick={() => create("copy")} disabled={busy}
              data-testid="request-copy-btn"
              className="h-11 bg-slate-900 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center justify-center gap-1 disabled:opacity-50">
              <Copy size={12}/> Get Copy-Paste Link
            </button>
            <button onClick={() => create("email")} disabled={busy || !tenantEmail.trim()}
              data-testid="request-email-btn"
              className="h-11 bg-emerald-600 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center justify-center gap-1 disabled:opacity-50">
              <EnvelopeSimple size={12}/> Email Tenant
            </button>
          </div>

          {result?.link && (
            <div className="p-3 bg-slate-50 border-2 border-slate-900" data-testid="request-result">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-700">Magic link ready</div>
              <div className="mt-2 flex items-center gap-1.5 bg-white p-1.5 border-2 border-slate-300">
                <LinkSimple size={12} className="text-slate-500"/>
                <input value={result.link} readOnly data-testid="new-link"
                  className="flex-1 text-[11px] font-mono outline-none min-w-0"/>
                <button onClick={() => copy(result.link)} data-testid="copy-new-link"
                  className="px-2 h-7 bg-slate-900 text-white text-[9px] font-black uppercase tracking-widest">
                  Copy
                </button>
              </div>
              <div className="mt-2 text-[10px] text-slate-500">Expires in 7 days · Paste into your text app / SMS / iMessage.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
